import { Badge, Button, Notice, Panel, Progress } from '@efira/ui';

import type { QuotaPayload } from '../api/queries';

/**
 * Остаток дневной квоты — подробно.
 *
 * ЧТО ЗДЕСЬ ЕСТЬ И ЧЕГО НЕТ У КОНКУРЕНТА. У него сообщение об исчерпании не
 * содержит ни остатка, ни времени сброса (`PRICING_LIMITS.md`, 2.1): стример
 * узнаёт, что лимит кончился, но не узнаёт, ждать ему час или до утра. Сброс
 * при этом идёт в полночь UTC — в Душанбе это 05:00, то есть посреди ночного
 * эфира.
 *
 * У нас сброс по местному времени, и момент называется ВСЕГДА, а не только при
 * исчерпании.
 *
 * Правило блока 5в остаётся: счётчик квоты стоит в точке принятия решения.
 * Решение «стоит ли начинать эфир» принимается здесь, в зоне эфира, а не в
 * модальном окне тарифа, куда надо сначала догадаться зайти.
 */
export interface QuotaPanelProps {
  quota: QuotaPayload;
  onUpgrade: () => void;
}

/** Момент сброса по местному времени пользователя. */
export function formatReset(quota: QuotaPayload): string {
  if (quota.resetsAt === null) {
    return '';
  }

  const reset = new Date(quota.resetsAt);

  const time = new Intl.DateTimeFormat('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: quota.timezone,
  }).format(reset);

  return `в ${time} по вашему времени`;
}

export function QuotaPanel({ quota, onUpgrade }: QuotaPanelProps) {
  // Pro: ограничения нет, и показывать нечего. Пустая полоса «осталось ∞»
  // занимала бы место и заставляла бы читать её каждый раз.
  if (quota.dailyMinutes === null) {
    return (
      <Panel title="Время эфира" subtitle="Тариф Pro: дневного лимита нет.">
        <p className="text-foreground/80 text-sm">
          Симулятор и эфир работают без ограничения по времени.
        </p>
      </Panel>
    );
  }

  const remaining = quota.remainingMinutes ?? 0;

  return (
    <Panel title="Время эфира" subtitle={`Сброс ${formatReset(quota)}`}>
      {quota.exhausted ? (
        <Notice tone="warning" title="Дневная квота на сегодня израсходована">
          Игра приостановлена, зритель видит на overlay короткое объяснение — эфир при этом
          продолжается. Симулятор работает: настроить игру и привязки можно прямо сейчас.
        </Notice>
      ) : null}

      {!quota.exhausted && quota.warning !== null ? (
        <Notice
          tone="warning"
          title={
            quota.warning === 2
              ? `Осталось ${remaining} мин — доигрывайте партию`
              : `Осталось ${remaining} мин`
          }
        >
          Когда время кончится, игра приостановится, а эфир продолжится. Сброс {formatReset(quota)}.
        </Notice>
      ) : null}

      <div className="flex flex-col gap-2">
        <div className="flex items-baseline gap-2">
          <span className="text-4xl font-bold tabular-nums">{remaining}</span>
          <span className="text-foreground/80">из {quota.dailyMinutes} мин на сегодня</span>
        </div>

        {/* Полоса показывает ОСТАТОК - ту же величину, что и число над ней.
            Полоса «израсходовано» при нуле выглядела пустой рядом с «60». */}
        {/* Полоса - `Progress` пресета: свой цвет по остатку она не несёт,
            его несёт плашка рядом. Прежняя редакция была `<progress>` с
            классами прежней библиотеки. */}
        <Progress
          value={Math.round((remaining / Math.max(1, quota.dailyMinutes)) * 100)}
          aria-label="Осталось минут эфира на сегодня"
        />

        <p className="text-foreground/80 text-sm">
          Считаются минуты подтверждённого эфира. Симулятор квоту не расходует.
        </p>
      </div>

      {quota.exhausted || quota.warning !== null ? (
        // Одно действие, и оно уместно именно здесь: ценность уже прожита -
        // игра только что работала. У конкурента этот момент потрачен впустую.
        <Button variant="primary" onClick={onUpgrade}>
          Перейти на Pro — без дневного лимита
        </Button>
      ) : null}
    </Panel>
  );
}

/** Компактный остаток для шапки: та же величина, другое место. */
export function QuotaChip({ quota }: { quota: QuotaPayload | undefined }) {
  if (quota === undefined) {
    return <Badge variant="secondary">…</Badge>;
  }

  if (quota.dailyMinutes === null) {
    return <Badge variant="secondary">без лимита</Badge>;
  }

  const remaining = quota.remainingMinutes ?? 0;

  return (
    <Badge
      variant={remaining <= 10 ? 'destructive' : 'secondary'}
      className="whitespace-nowrap tabular-nums"
      title={`Сброс ${formatReset(quota)}`}
      aria-label={`Осталось ${remaining} мин из ${quota.dailyMinutes}`}
    >
      {remaining} мин<span className="hidden sm:inline"> из {quota.dailyMinutes}</span>
    </Badge>
  );
}
